import { useState, useCallback, useRef } from 'react';

export const useDailyChangeTracker = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [marketData, setMarketData] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [uniqueDates, setUniqueDates] = useState(new Set());
  const [config, setConfig] = useState({
    daysToFetch: 5,
    minVolumeFilter: 1000000, // Minimum volume filter
    sortBy: 'symbol' // symbol, avgChange, totalChange, volatility
  });

  const abortControllerRef = useRef(null);

  const BASE_URL = 'https://fapi.binance.com';
  const KLINE_ENDPOINT = '/fapi/v1/klines';

  const fetchSymbolDailyData = useCallback(async (symbol, signal, daysToFetch) => {
    try {
      // Use a higher limit to ensure we get enough data, then slice to what we need
      const limitToFetch = Math.max(daysToFetch + 2, 10); // Add buffer for weekends/holidays
      
      const response = await fetch(
        `${BASE_URL}${KLINE_ENDPOINT}?symbol=${symbol}&interval=1d&limit=${limitToFetch}`,
        { signal }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      
      const klineData = await response.json();
      const records = [];
      const dates = new Set();
      
      // Process the requested number of days from the end
      const lastNDays = klineData.slice(-daysToFetch);
      
      for (const d of lastNDays) {
        const timestamp = d[0];
        const dateObj = new Date(timestamp);
        const dateStr = dateObj.toISOString().split('T')[0];
        dates.add(dateStr);
        
        const openPrice = parseFloat(d[1]);
        const highPrice = parseFloat(d[2]);
        const lowPrice = parseFloat(d[3]);
        const closePrice = parseFloat(d[4]);
        const volume = parseFloat(d[5]);
        const quoteVolume = parseFloat(d[7]);
        
        const pctChange = ((closePrice - openPrice) / openPrice) * 100;
        const dailyRange = ((highPrice - lowPrice) / openPrice) * 100;
        
        records.push({
          symbol,
          date: dateStr,
          timestamp,
          pctChange: parseFloat(pctChange.toFixed(2)),
          openPrice,
          closePrice,
          highPrice,
          lowPrice,
          volume,
          quoteVolume,
          dailyRange: parseFloat(dailyRange.toFixed(2))
        });
      }
      
      return { records, dates };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log(`Fetch aborted for ${symbol}`);
        return null;
      }
      console.error(`Error fetching data for ${symbol}:`, error);
      return null;
    }
  }, []); // Remove config.daysToFetch dependency

  const calculateSymbolStats = useCallback((symbolRecords) => {
    if (!symbolRecords || symbolRecords.length === 0) return null;
    
    const changes = symbolRecords.map(r => r.pctChange);
    const volumes = symbolRecords.map(r => r.quoteVolume);
    
    const totalChange = changes.reduce((sum, change) => sum + change, 0);
    const avgChange = totalChange / changes.length;
    const avgVolume = volumes.reduce((sum, vol) => sum + vol, 0) / volumes.length;
    
    // Calculate volatility (standard deviation)
    const variance = changes.reduce((sum, change) => sum + Math.pow(change - avgChange, 2), 0) / changes.length;
    const volatility = Math.sqrt(variance);
    
    // Get latest price info
    const latestRecord = symbolRecords[symbolRecords.length - 1];
    
    return {
      symbol: symbolRecords[0].symbol,
      records: symbolRecords,
      totalChange: parseFloat(totalChange.toFixed(2)),
      avgChange: parseFloat(avgChange.toFixed(2)),
      avgVolume,
      volatility: parseFloat(volatility.toFixed(2)),
      latestPrice: latestRecord.closePrice,
      latestDate: latestRecord.date,
      daysCount: symbolRecords.length
    };
  }, []);

  const loadSymbols = useCallback(async () => {
    try {
      const response = await fetch('https://fapi.binance.com/fapi/v1/exchangeInfo');
      const data = await response.json();
      const symbolList = data.symbols
        .filter(symbol => 
          symbol.quoteAsset === 'USDT' && 
          symbol.status === 'TRADING' && 
          symbol.contractType === 'PERPETUAL'
        )
        .map(symbol => symbol.symbol)
        .sort();
      setSymbols(symbolList);
      console.log(`✅ Loaded ${symbolList.length} USDT perpetual pairs`);
      return symbolList;
    } catch (error) {
      console.error('❌ Failed to load symbols:', error);
      return [];
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setProgress(0);
    setProgressMessage('Loading symbols...');
    
    try {
      // Create abort controller for this fetch session
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      
      let symbolList = symbols;
      if (symbolList.length === 0) {
        symbolList = await loadSymbols();
      }
      
      if (symbolList.length === 0) {
        throw new Error('No symbols loaded');
      }
      
      setProgressMessage('Fetching daily data...');
      
      const allRecords = [];
      const allDates = new Set();
      let processedCount = 0;
      const totalCount = symbolList.length;
      const batchSize = 10; // Process in batches to avoid overwhelming the API
      
      // Get current config values to avoid stale closure
      const currentDaysToFetch = config.daysToFetch;
      const currentMinVolumeFilter = config.minVolumeFilter;
      
      // Process symbols in batches
      for (let i = 0; i < symbolList.length; i += batchSize) {
        if (signal.aborted) break;
        
        const batch = symbolList.slice(i, i + batchSize);
        const batchPromises = batch.map(symbol => fetchSymbolDailyData(symbol, signal, currentDaysToFetch));
        const batchResults = await Promise.all(batchPromises);
        
        for (const result of batchResults) {
          if (result && result.records) {
            allRecords.push(...result.records);
            result.dates.forEach(date => allDates.add(date));
          }
          processedCount++;
          
          const progressPercent = Math.round((processedCount / totalCount) * 100);
          setProgress(progressPercent);
          setProgressMessage(`Fetching: ${processedCount}/${totalCount} (${progressPercent}%)`);
        }
        
        // Add delay between batches to avoid rate limits
        if (i + batchSize < symbolList.length && !signal.aborted) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (signal.aborted) {
        setProgressMessage('Fetch cancelled');
        return;
      }
      
      setProgressMessage('Processing data...');
      
      // Group records by symbol and calculate stats
      const symbolGroups = {};
      allRecords.forEach(record => {
        if (!symbolGroups[record.symbol]) {
          symbolGroups[record.symbol] = [];
        }
        symbolGroups[record.symbol].push(record);
      });
      
      // Calculate stats for each symbol
      const processedData = [];
      Object.keys(symbolGroups).forEach(symbol => {
        const symbolRecords = symbolGroups[symbol].sort((a, b) => a.timestamp - b.timestamp);
        const stats = calculateSymbolStats(symbolRecords);
        
        if (stats && stats.avgVolume >= currentMinVolumeFilter) {
          processedData.push(stats);
        }
      });
      
      // Sort data - use current config value
      processedData.sort((a, b) => {
        switch (config.sortBy) {
          case 'avgChange':
            return Math.abs(b.avgChange) - Math.abs(a.avgChange);
          case 'totalChange':
            return Math.abs(b.totalChange) - Math.abs(a.totalChange);
          case 'volatility':
            return b.volatility - a.volatility;
          case 'volume':
            return b.avgVolume - a.avgVolume;
          default:
            return a.symbol.localeCompare(b.symbol);
        }
      });
      
      setMarketData(processedData);
      setUniqueDates(allDates);
      setProgressMessage(`Completed: ${processedData.length} symbols analyzed`);
      console.log(`✅ Processed ${processedData.length} symbols with daily data`);
      
    } catch (error) {
      console.error('❌ Failed to fetch daily data:', error);
      setProgressMessage(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [isLoading, symbols, config, loadSymbols, fetchSymbolDailyData, calculateSymbolStats]);

  const cancelFetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
      setProgressMessage('Cancelled by user');
    }
  }, []);

  const updateConfig = useCallback((newConfig) => {
    setConfig(prev => ({ ...prev, ...newConfig }));
    
    // If daysToFetch changed and we have existing data, optionally clear it
    // This forces a re-fetch with the new configuration
    if (newConfig.daysToFetch !== undefined && newConfig.daysToFetch !== config.daysToFetch) {
      console.log(`📊 Days to fetch changed from ${config.daysToFetch} to ${newConfig.daysToFetch}`);
      // Optionally clear existing data to force re-fetch
      // setMarketData([]);
      // setUniqueDates(new Set());
    }
  }, [config.daysToFetch]);

  const getDateColumns = useCallback(() => {
    return Array.from(uniqueDates).sort().slice(-config.daysToFetch);
  }, [uniqueDates, config.daysToFetch]);

  const init = useCallback(async () => {
    console.log('📊 Initializing Daily Change Tracker...');
    await loadSymbols();
  }, [loadSymbols]);

  // Helper function to re-filter existing data with new volume filter
  const refilterData = useCallback(() => {
    if (marketData.length === 0) return;
    
    const filtered = marketData.filter(item => item.avgVolume >= config.minVolumeFilter);
    
    // Sort data according to current sortBy setting
    filtered.sort((a, b) => {
      switch (config.sortBy) {
        case 'avgChange':
          return Math.abs(b.avgChange) - Math.abs(a.avgChange);
        case 'totalChange':
          return Math.abs(b.totalChange) - Math.abs(a.totalChange);
        case 'volatility':
          return b.volatility - a.volatility;
        case 'volume':
          return b.avgVolume - a.avgVolume;
        default:
          return a.symbol.localeCompare(b.symbol);
      }
    });
    
    setMarketData(filtered);
  }, [marketData, config.minVolumeFilter, config.sortBy]);

  return {
    isLoading,
    marketData,
    symbols,
    progress,
    progressMessage,
    config,
    uniqueDates: Array.from(uniqueDates).sort(),
    fetchAllData,
    cancelFetch,
    updateConfig,
    getDateColumns,
    refilterData,
    init
  };
};